import React, { useState } from 'react'
import { keyframes } from '@emotion/react'
import Stack from '@mui/joy/Stack'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import Alert from '@mui/joy/Alert'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useTranslation, Trans } from 'react-i18next'

import GarageCard from './garage-card'
import GarageCardSkeleton from './garage-card-skeleton'
import type {
    HoverPoint,
    Garage,
    GarageAvailability,
    SelectedPoint,
    Currency,
} from '../types'
import { getLaterStartOptions } from '../pwa/later-availability'

export default function GarageList({
    garageList,
    loadingGarageList,
    loadingGarageAvailability,
    hoverPoint,
    setHoverPoint,
    setSelectedId,
    garagesAvailableLater,
    errors,
    currency,
    navigateToGarage,
} : {
    garageList: Array<Garage>,
    loadingGarageList: boolean,
    loadingGarageAvailability: boolean,
    onMap?: boolean,
    hoverPoint?: HoverPoint | null,
    setHoverPoint?: ((arg1: HoverPoint | null) => void) | null,
    setSelectedId?: ((arg1: SelectedPoint | null) => void) | null,
    garagesAvailableLater: GarageAvailability | null,
    errors?: Array<string> | null,
    currency: Currency,
    navigateToGarage?: ((arg1: number) => void) | null | undefined,
}) {

    const { t } = useTranslation()

    const [dismissedSearchKey, setDismissedSearchKey] = useState<string | null>(null)
    const promptDismissed = garagesAvailableLater?.searchKey === dismissedSearchKey

    const candidateStartTimes = getLaterStartOptions(garagesAvailableLater?.searchedStartTime)
    const firstCandidateStartTime = candidateStartTimes[0]
    const lastCandidateStartTime = candidateStartTimes[candidateStartTimes.length - 1]
    const singlePromptOption = candidateStartTimes.length === 1
        ? garagesAvailableLater?.options.find(option => option.startTime === firstCandidateStartTime)
        : undefined

    const zoomIn = keyframes`
        from {
            opacity: 0;
            transform: scale(0.9, 0.9);
        }
        to {
            opacity: 1;
            transform: scale(1, 1);
        }
    `

    return (
        <Box sx={{ width: '100%' }}>
            {errors && errors?.length > 0 &&
                <Box sx={{
                    mb: '0.5rem',
                    animation: `${zoomIn} 0.3s ease`,
                }}>
                    <Alert
                        variant="outlined"
                        color="danger"
                        sx={{
                            backgroundColor: 'var(--joy-palette-danger-100)',
                        }}
                    >
                        <Stack sx={{ width: '100%', minWidth: 0, gap: '0.5rem', textAlign: 'left' }}>
                            {errors.map((error, index) => (
                                <Typography key={index} level="body-sm" color="danger" sx={{ overflowWrap: 'anywhere' }}>
                                    {error}
                                </Typography>
                            ))}
                        </Stack>
                    </Alert>
                </Box>
            }
            {garagesAvailableLater && !promptDismissed &&
                <Box sx={{
                    mb: '0.5rem',
                    animation: `${zoomIn} 0.3s ease`,
                }}>
                    <Alert
                        variant="outlined"
                        color="warning"
                        sx={{
                            position: 'relative',
                            backgroundColor: 'var(--joy-palette-secondary-100)',
                        }}
                    >
                        <IconButton
                            variant="plain"
                            size="sm"
                            aria-label={t('general.close')}
                            onClick={() => setDismissedSearchKey(garagesAvailableLater.searchKey)}
                            sx={{
                                position: 'absolute',
                                top: '0.25rem',
                                right: '0.25rem',
                                color: 'primary.900',
                                '&& svg': {
                                    fill: 'currentColor',
                                },
                                '&&:hover': {
                                    color: 'primary.900',
                                },
                                '&&:hover svg': {
                                    fill: 'currentColor',
                                },
                            }}
                        >
                            <CloseRoundedIcon />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography level="body-sm" sx={{ color: 'black', pr: 4 }}>
                                {singlePromptOption ?
                                    <Trans
                                        i18nKey="general.garagesAvailableAtTime"
                                        count={singlePromptOption.diff}
                                        values={{ time: singlePromptOption.startTime }}
                                    />
                                :
                                    <Trans
                                        i18nKey="general.garagesAvailableLaterGeneric"
                                        values={{
                                            from: firstCandidateStartTime,
                                            to: lastCandidateStartTime,
                                        }}
                                    />
                                }
                            </Typography>
                            <Box sx={{
                                mt: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1,
                            }}>
                                {singlePromptOption ?
                                    <Button
                                        variant="solid"
                                        size="sm"
                                        onClick={() => garagesAvailableLater.onSelectStartTime(singlePromptOption.startTime)}
                                    >
                                        {t('general.moveToTimeWithCount', {
                                            time: singlePromptOption.startTime,
                                            count: singlePromptOption.diff,
                                        })}
                                    </Button>
                                :
                                    garagesAvailableLater.options.map(option =>
                                        <Button
                                            key={option.startTime}
                                            variant="solid"
                                            size="sm"
                                            onClick={() => garagesAvailableLater.onSelectStartTime(option.startTime)}
                                        >
                                            {t('general.moveToTimeWithCount', {
                                                time: option.startTime,
                                                count: option.diff,
                                            })}
                                        </Button>
                                    )
                                }
                            </Box>
                        </Box>
                    </Alert>
                </Box>
            }
            <Stack spacing={1}>
                {!loadingGarageList && garageList.map(garage =>
                    <GarageCard
                        key={garage.id}
                        garage={garage}
                        availableLaterAt={garagesAvailableLater?.garages.find(
                            item => item.garageId === garage.id,
                        )?.earliestAvailableTime}
                        loadingGarageAvailability={loadingGarageAvailability}
                        onMap={false}
                        hoverPoint={hoverPoint}
                        setHoverPoint={setHoverPoint}
                        setSelectedId={setSelectedId}
                        currency={currency}
                        navigateToGarage={navigateToGarage}
                    />
                )}
                {!loadingGarageList && garageList.length === 0 &&
                    <Typography level='title-md' textAlign='center'>{t('filters.noResultsMatch')}</Typography>
                }
                {loadingGarageList && [...Array(15)].map((_cell, i) =>
                    <GarageCardSkeleton key={i}/>
                )}
            </Stack>
            <Box
                sx={{
                    display: {
                        xs: 'block',
                        md: 'none',
                    },
                    paddingBottom: '4.5rem',
                }}
            />
        </Box>
    )
}
